import { AllFavoritesResponse } from '@@types/Api/Channel';
import Channel, { Management, SharedTenantInfo, ThreadChannel } from '@@types/Channel';
import { CHANNEL_ID, MEMBER_ID } from '@@types/common';
import Member from '@@types/Member';

import {
  INVITE_CAPACITY_LIMIT,
  INVITE_CAPACITY_LIMIT_LITE,
  MANAGE_HIDDEN_CHANNEL_TAB,
  NOTIFICATION,
  PANEL_TYPE,
} from '~/constants';
import { PreferenceCategory } from '~/constants/member';
import { WinNativeFocusTarget } from '~/constants/nativeApi';
import i18n from '~/i18n';
import {
  getNoMemberTitle,
  isBotChannel,
  isDirectChannel,
  isDummyChannelId,
  isMainThreadChannel,
  isMeChannel,
  isSharedChannel,
  isThreadChannel,
  isTopicChannel,
  isUnreadHiddenChannel,
} from '~/services/channelService';
import GA from '~/services/GA/GA';
import popupWindowService from '~/services/popupWindowService';
import { channelMailSettingStore } from '~/stores/ChannelMailSettingStore';
import { channelManager } from '~/stores/ChannelManager';
import { channelNoticesStore } from '~/stores/ChannelNoticesStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { channelTenantsStore } from '~/stores/ChannelTenantsStore';
import { mainDetailUiState } from '~/stores/DetailUiState';
import { favoritesStore } from '~/stores/FavoritesStore';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { api } from '~/stores/providers/Api2';
import { interTenantApi } from '~/stores/providers/InterTenantApi';
import { uiState } from '~/stores/UiState';
import { userStore } from '~/stores/UserStore';
import { makeThreadChannel, showCapacityAlert, validateCapacity } from '~/utils/channelUtil';
import { isNonEmpty, isNonNil, isTypeOf } from '~/utils/fnUtil/fnUtil';
import { getLeaverOrDormantText, getMemberFullName } from '~/utils/memberUtil';
import { mEither, mMaybe, then } from '~/utils/monadUtil';
import { isElectron, isWinNative } from '~/utils/runEnvironmentUtil';
import { getPreferenceValueByCategory } from '~/utils/settingUtil';
import Future, { encaseP, FutureInstance } from 'fluture';
import { isEmpty, noop } from 'lodash';
import {
  always,
  anyPass,
  assocPath,
  concat,
  cond,
  converge,
  curry,
  equals,
  filter,
  find,
  flatten,
  identity,
  ifElse,
  isNil,
  join,
  juxt,
  map,
  path,
  pick,
  pipe,
  pluck,
  prop,
  propEq,
  propOr,
  reject,
  replace,
  symmetricDifference,
  T,
  tap,
  useWith as ramdaUseWith,
  when,
} from 'ramda';
import { Either, Maybe } from 'ramda-fantasy';

import Actions from '../Actions';
import { stores } from '../Stores';

type SelectChannelParams = {
  forwardLogId?: string;
  currentContext?: 'main' | 'thread';
};

type TitleParams = {
  channel?: Channel;
  channelId?: string;
  includeUser?: boolean;
};

export const createChannelActions = (actions: Actions) => {
  const selectChannel = (channelId: CHANNEL_ID, params?: SelectChannelParams) => {
    uiState.setCurrentContext(params?.currentContext ?? 'main');
    if (uiState.isMainView) {
      mainDetailUiState.setCurrentChannelId(channelId);
    } else {
      actions.openPopupChannel(channelId, params);
    }
  };

  const selectChannelByNoti = (channelId: string) => {
    if (channelsStore.getById(channelId)) {
      selectChannel(channelId);
      GA.writeSelectChannel('노티피케이션');
    } else {
      uiState.setPanelType(PANEL_TYPE.NONE);
      actions.threadChannelActions.openThreadChannel(channelId).then((channel) => {
        selectChannel(channel.parentChannelId, { currentContext: 'thread' });
        actions.threadMainEditorActions.focus();
      });
      GA.writeSelectChannel('스레드 노티피케이션');
    }
  };

  const checkAdminMode = (channelId: string) => {
    const channel = channelsStore.getById(channelId);
    return channel?.management?.adminOnly ?? false;
  };

  const setFavoritesFromResponse = (response: AllFavoritesResponse) => {
    pipe(path(['shortcuts', 'favorites']), favoritesStore.setFavorites)(response);
  };

  const showUnreadHiddenChannels = (channels) => {
    pipe(
      filter(isUnreadHiddenChannel),
      when(
        isNonEmpty,
        ramdaUseWith(
          map((channelId: string) => api.channel.updateChannelDisplayed(channelId, true)),
          [pluck('channelId')]
        )
      )
    )(channels);
  };

  const setChannelsFromResponse = (response) => {
    pipe(
      propOr([], 'channels'),
      juxt([
        (channels) => {
          channelsStore.mergeChannels([...channels, makeThreadChannel()]);
        },
        showUnreadHiddenChannels,
      ])
    )(response);
  };

  const createDirectChannel = (opponentId, selectAfterCreate = false) => {
    return api.channel.createDirectChannel(userStore.orgId, opponentId, selectAfterCreate);
  };

  const validatePrivateCapacity = (memberIds: string[]) => {
    const capacity = globalSettingStore.isLiteVersion ? INVITE_CAPACITY_LIMIT_LITE : INVITE_CAPACITY_LIMIT;
    return validateCapacity(capacity, memberIds);
  };

  const createGroupChannel = (params: { memberIds: string[] }) => {
    return validatePrivateCapacity(params.memberIds).then(
      () => api.channel.createPrivateChannel(userStore.orgId, params),
      showCapacityAlert
    );
  };

  const fetchAndCache = () => {
    return api.channel.fetchChannelsAndFavorites().then((response) => {
      setChannelsFromResponse(response);
      setFavoritesFromResponse(response);
    });
  };

  const fetchSharedTenantsList = (channelId: CHANNEL_ID) => {
    return interTenantApi.channel.fetchChannelTenantList(channelId).then((tenantList: SharedTenantInfo[]) => {
      pipe<any, any, any, any>(
        pluck('organizationMemberIds'),
        flatten,
        channelsStore.updateMembers(channelId)
      )(tenantList);
      channelTenantsStore.setTenantInfoList(channelId, tenantList);
    });
  };

  const fetchAndCacheInterTenantChannels = () => {
    return interTenantApi.channel.fetchChannels().then(
      (channels) => {
        channelsStore.mergeChannels(channels as Channel[]);
      },
      () => {}
    );
  };

  const getOrCreateByOpponentId = async (opponentId: string, selectAfterCreate?: boolean): Promise<Channel> => {
    const channel = channelsStore.getByOpponentId(opponentId);
    if (channel) {
      return Promise.resolve(channel);
    }

    const createdChannel = await api.channel.getOrCreateDirectChannel(userStore.orgId, opponentId, selectAfterCreate);
    channelsStore.addChannel(createdChannel);
    return createdChannel;
  };

  const getOrCreateDirectChannelByChannelId = (channelId, selectAfterCreate = false): Promise<Channel> => {
    if (isDummyChannelId(channelId)) {
      return pipe(replace('dummy-', ''), (opponentId) => getOrCreateByOpponentId(opponentId, selectAfterCreate))(
        channelId
      );
    } else {
      return Promise.resolve(channelsStore.getById(channelId));
    }
  };

  const isSameChannelMembers = curry((membersId: MEMBER_ID[], channel: Channel) => {
    return pipe(symmetricDifference(channel.members), equals([userStore.id]))(membersId);
  });

  const findIncludingMembers = (
    memberIds: MEMBER_ID[]
  ): Either<{ error: 'noMatch'; memberIds: MEMBER_ID[] }, Channel> => {
    const channel = find(isSameChannelMembers(memberIds), channelsStore.nonTopicChannels);
    return isNil(channel) ? Either.Left({ error: 'noMatch', memberIds }) : Either.Right(channel);
  };

  const getOrCreateGroup = (memberIds: MEMBER_ID[]): Promise<Channel> => {
    return pipe<any, any, any>(
      findIncludingMembers,
      mEither(({ memberIds }) => createGroupChannel({ memberIds }), Promise.resolve.bind(Promise))
    )(memberIds) as Promise<Channel>;
  };

  const selectMeChannel = () => {
    const { meChannel } = channelsStore;
    if (isNonNil(meChannel)) {
      return selectChannel(meChannel.channelId);
    }
  };

  const selectOrCreateDirectChannelByOpponentId = async (opponentId: string, params?: SelectChannelParams) => {
    if (opponentId === userStore.id) {
      return selectMeChannel();
    }

    const channel = channelsStore.getByOpponentId(opponentId);
    if (channel) {
      return selectChannel(channel.channelId, params);
    }

    const createdChannel = await api.channel.getOrCreateDirectChannel(userStore.orgId, opponentId, true);
    channelsStore.addChannel(createdChannel);
    selectChannel(createdChannel.channelId, params);
    return createdChannel;
  };

  const selectOrCreate = (channel: Channel, params) => {
    if (isDirectChannel(channel)) {
      return selectOrCreateDirectChannelByOpponentId(channel.opponentId, params);
    } else {
      selectChannel(channel.channelId, params);
    }
  };

  const toggleDisplay = async (channelId: string) => {
    const channel = await getOrCreateDirectChannelByChannelId(channelId, false);
    api.channel.updateChannelDisplayed(channel.channelId, !channel.displayed);
  };

  const toggleNotificationValue = (notification: NOTIFICATION) => {
    return notification === NOTIFICATION.ON ? NOTIFICATION.OFF : NOTIFICATION.ON;
  };

  const updateNotification = curry((channelId: CHANNEL_ID, value) => {
    pipe<any, any, any, any, any>(
      channelsStore.safeGetById,
      map(propOr([], 'preferences')),
      map(map(ifElse(propEq('category', 'notification'), assocPath(['value', 'notification'], value), identity))),
      mEither(identity, api.me.updateChannelPreferences(channelId))
    )(channelId);
  });

  const toggleNotification = async (channelId: CHANNEL_ID) => {
    // prettier-ignore
    const channel: Channel = await getOrCreateDirectChannelByChannelId(channelId, false);

    pipe<any, any, any, any, any>(
      prop('preferences'),
      getPreferenceValueByCategory(PreferenceCategory.NOTIFICATION),
      toggleNotificationValue,
      updateNotification(channel.channelId)
    )(channel);
  };

  const toggleTranslation = (channelId: string) => {
    const channel = channelsStore.getById(channelId);
    const value = !propEq('translate', 1, channel);
    api.channel.updateChannelTranslation(channelId, value);
  };

  const updateAdminOnly = (channel: Channel, adminOnly: boolean) => {
    const management = pick(['adminOnly'], channel.management);
    management.adminOnly = adminOnly;
    api.channel.updateChannelManagement(channel.channelId, management as Management);
  };

  const updateTitleAndDescription = curry((channelId, params) => {
    ifElse(
      isSharedChannel,
      () => interTenantApi.channel.updateChannel(channelId, params),
      () => api.channel.updateChannel(channelId, params)
    )(channelsStore.getById(channelId));
  });

  const uploadImage = (channelId: string, image: File): FutureInstance<never, void> => {
    return Future((reject, resolve) => {
      actions.proxyActions.uploadChannelImage(channelId, image).catch(reject).then(resolve);
    });
  };

  const fetchChannelMailCandidate = (channelId) => {
    return encaseP(channelMailSettingStore.fetchCandidate)(channelId);
  };

  const fetchMailSetting = (channelId: string) => {
    if (globalSettingStore.isLiteVersion) return;
    api.channel.fetchChannelMailSetting(channelId).then(channelMailSettingStore.setMailSetting(channelId), noop);
  };

  const submitMailChannelSettingModal = (params) => {
    const { channelId } = params;
    pipe(
      channelMailSettingStore.getMailSetting,
      propOr('', 'emailAddress'),
      ifElse(
        isNonEmpty,
        () => api.channel.updateChannelMail(params),
        () => api.channel.registerChannelMail(params)
      ),
      then(channelMailSettingStore.setMailSetting(channelId))
    )(channelId);
  };

  const fetchChannelNotices = async (channelId: CHANNEL_ID) => {
    try {
      const notices = await api.notice.fetchChannelNotices(channelId);
      channelNoticesStore.setChannelNotices(notices);
    } catch (e) {}
  };

  const fetchChannelPostNotice = (channelId: CHANNEL_ID) => {
    api.notice.fetchChannelPostNotice(channelId).then(channelNoticesStore.setPostedNotice, noop);
  };

  const makePopupUrl = (channelId: CHANNEL_ID) => {
    return stores.isSharedContentsChannel
      ? `/messenger/popup/${channelId}?_share=true`
      : `/messenger/popup/${channelId}`;
  };

  const openPopupChannel = async (channelId) => {
    if (isElectron()) {
      const channel = await actions.channelActions.getOrCreateDirectChannelByChannelId(channelId);
      return popupWindowService.open(makePopupUrl(channel.channelId), channel.channelId);
    } else {
      const { channelId: realChannelId } = await actions.channelActions.getOrCreateDirectChannelByChannelId(channelId);
      const popupWindow = popupWindowService.restoreWindowRef(realChannelId);
      if (isNil(popupWindow)) return null;

      if (popupWindow.location.href === 'about:blank') {
        const url = makePopupUrl(realChannelId);
        return isWinNative()
          ? popupWindowService.open(url, realChannelId, { width: 0, height: 0 })
          : popupWindowService.open(url, realChannelId);
      }

      popupWindow.name = realChannelId;
      isWinNative()
        ? actions.nativeActions.sendSetFocus({
            channelId: realChannelId,
            target: WinNativeFocusTarget.POPUP,
          })
        : popupWindow.focus();
      return Promise.resolve(popupWindow);
    }
  };

  const getFullName = pipe<any, Maybe<Member>, string>(
    interTenantMembersStore.safeGetById,
    mMaybe(
      '',
      converge<
        [{ name?: string; nickname?: string; tenantMemberRole?: string; status?: string }],
        string,
        string,
        string
      >(concat, [getMemberFullName, getLeaverOrDormantText])
    )
  );

  const fetchUnfetchedMembers = (memberIds: MEMBER_ID[]) => {
    actions.memberActions.fetchBulkMembers(memberIds);
  };

  const fetchUnfetchedMember = (memberId: MEMBER_ID) => {
    fetchUnfetchedMembers([memberId]);
  };

  const getUserFullName = pipe<[{ memberId?: string }], string, string>(prop('memberId'), getFullName);

  const getOpponentFullName = pipe<any, string, string, string>(
    prop('opponentId'),
    tap(fetchUnfetchedMember),
    getFullName
  );

  const getOtherMembersName = pipe(tap(fetchUnfetchedMembers), map(getFullName), join(', '));

  const getTitle = (params: TitleParams) => {
    const getMembersName = pipe<any, MEMBER_ID[], MEMBER_ID[], string>(
      propOr([], 'members'),
      when(() => !(params.includeUser ?? false), reject(equals(userStore.id))),
      ifElse(isEmpty, getNoMemberTitle, getOtherMembersName)
    );

    const getTitleByChannel: (channel: Channel) => string = cond<[Channel], string>([
      [anyPass([isBotChannel, isMainThreadChannel, isTopicChannel, isThreadChannel]), prop('title')],
      [isMeChannel, getUserFullName],
      [isDirectChannel, getOpponentFullName],
      [T, getMembersName],
    ]);

    const getTitleByChannelId = pipe(channelsStore.safeGetById, mEither(always(''), getTitleByChannel));

    return ifElse<any, any, any>(
      isTypeOf('channel') as (value: Record<string, unknown>) => boolean,
      pipe(prop('channel'), getTitleByChannel),
      pipe(prop('channelId'), getTitleByChannelId)
    )(params);
  };

  const unregisterChannelMail = async (channelId: string) => {
    await api.channel.unregisterChannelMail(channelId);
    channelMailSettingStore.clearMailSetting(channelId);
  };

  const updateTranslationLanguage = (channelId: CHANNEL_ID, value: string) => {
    pipe<any, any, any, any, void>(
      channelsStore.safeGetById,
      map(propOr([], 'preferences')),
      map(map(ifElse(propEq('category', 'lang'), assocPath(['value', 'lang'], value), identity))),
      mEither(identity, api.me.updateChannelPreferences(channelId))
    )(channelId);
  };

  const createOrSelectChannel = async (c: Channel) => {
    if (c.channelId === mainDetailUiState.contentsChannelId) {
      await actions.logActions.fetchLatestLogsOrScrollBottom(c.channelId);
    } else if (isDirectChannel(c)) {
      // memberId가 같은 채널이 2개 이상인 경우가 있어서 예외처리
      if (channelsStore.getById(c.channelId)) {
        selectChannel(c.channelId);
        return c.channelId;
      }
      const { channelId } = await getOrCreateByOpponentId(c.opponentId, false);
      selectChannel(channelId);
      return channelId;
    } else {
      selectChannel(c.channelId);
    }

    return c.channelId;
  };

  const validateMaxMembers = async (channelId, inviteeCount: number) => {
    const channel = channelsStore.getById(channelId);
    if (isEmpty(channel.members)) {
      await actions.channelActions.fetchSharedTenantsList(channel.channelId);
    }
    if (channel.members.length + inviteeCount > channel.capacity) {
      actions.modalActions.openAlert({
        description: i18n.t('CHANNEL_CAPACITY_IS_FULL_ERROR', {
          max: channel.capacity,
        }),
      });
      return false;
    }
    return true;
  };

  const copyAndArchiveChannel = async (channelId: string, invitee: string[]) => {
    if (userStore.isGuest || !(await validateMaxMembers(channelId, invitee.length))) {
      return;
    }

    const isAdminMode = checkAdminMode(channelId);
    if (isAdminMode && !channelsStore.isAdmin(channelId, userStore.id)) {
      const description = i18n.t('invite.channel.adminAlert');
      actions.modalActions.openAlert({ description });
      return;
    }
    const copiedChannel = await api.channel.copyAndArchiveChannel(channelId);
    // 이 때에는 아직 channelsStore에 정보가 없고 만든 사람은 admin 이기 때문에 바로 인원 초대
    actions.proxyActions.inviteChannelMembers(copiedChannel.channelId, invitee);
  };

  const handleClickHiddenChannelTab = ({ id: tabId }: { id: MANAGE_HIDDEN_CHANNEL_TAB }) => {
    uiState.setManageHiddenChannelTab(tabId);
  };

  const handleClickArchivedChannelTab = ({ id: tabId }: { id: MANAGE_HIDDEN_CHANNEL_TAB }) => {
    uiState.setManageHiddenChannelTab(tabId);
  };

  const isMeChannelId = (channelId: CHANNEL_ID) => {
    return channelsStore.meChannel?.channelId === channelManager.getMainChannelId(channelId);
  };

  const canUploadChannel = (channel: Channel | ThreadChannel) => {
    if (isNil(channel)) {
      return false;
    }
    return !isMainThreadChannel(channel) && !actions.permissionActions.isReadonlyChannel(channel);
  };

  return {
    copyAndArchiveChannel,
    checkAdminMode,
    createDirectChannel,
    createGroupChannel,
    createOrSelectChannel,
    fetchAndCache,
    fetchAndCacheInterTenantChannels,
    fetchChannelMailCandidate,
    fetchChannelNotices,
    fetchChannelPostNotice,
    fetchMailSetting,
    fetchSharedTenantsList,
    getOrCreateByOpponentId,
    getOrCreateDirectChannelByChannelId,
    getOrCreateGroup,
    getTitle,
    handleClickArchivedChannelTab,
    handleClickHiddenChannelTab,
    openPopupChannel,
    selectChannel,
    selectChannelByNoti,
    selectMeChannel,
    selectOrCreate,
    selectOrCreateDirectChannelByOpponentId,
    submitMailChannelSettingModal,
    toggleDisplay,
    toggleNotification,
    toggleTranslation,
    unregisterChannelMail,
    updateAdminOnly,
    updateNotification,
    updateTitleAndDescription,
    updateTranslationLanguage,
    uploadImage,
    isMeChannelId,
    canUploadChannel,
  };
};
